import React, { useEffect, useState } from "react";
import { Alert, Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { authRequest } from "../services/auth";

const COGNIZANT_EMAIL = /^[^\s@]+@cognizant\.com$/i;

function normalizeRecipients(values) {
  return [...new Set(values
    .flatMap((value) => String(value || "").split(/[\s,;]+/))
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean))];
}

export default function ShareResultDialog({ open, onClose, title, resultType, buildAttachment }) {
  const [recipients, setRecipients] = useState([]);
  const [recipientInput, setRecipientInput] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!open) return;
    setRecipients([]); setRecipientInput(""); setError(""); setSuccess("");
    setSubject(`MatchIQ ${resultType}: ${title}`);
    setBody(`Hello,\n\nA ${resultType.toLowerCase()} from MatchIQ has been shared with you. The ${resultType.toLowerCase()} is attached.\n\nRegards,\nWorkforce Management`);
  }, [open, resultType, title]);

  const send = async () => {
    const emails = normalizeRecipients([...recipients, recipientInput]);
    if (!emails.length || emails.some((email) => !COGNIZANT_EMAIL.test(email))) {
      setError("Enter one or more valid @cognizant.com email addresses"); return;
    }
    if (emails.length > 20) {
      setError("You can email a maximum of 20 recipients at a time"); return;
    }
    setRecipients(emails); setRecipientInput("");
    setSending(true); setError(""); setSuccess("");
    try {
      const attachment = await buildAttachment();
      const { data } = await authRequest().post("/share-result", { recipients: emails, subject, body, attachment });
      setSuccess(data.message);
    } catch (sendError) { setError(sendError.response?.data?.error || sendError.message || "Unable to email this result"); }
    finally { setSending(false); }
  };

  const hasRecipient = normalizeRecipients([...recipients, recipientInput]).length > 0;

  return <Dialog open={open} onClose={sending ? undefined : onClose} fullWidth maxWidth="sm">
    <DialogTitle sx={{ fontWeight: 800 }}>Email this {resultType.toLowerCase()}</DialogTitle>
    <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
      <Typography variant="body2" color="text.secondary">Add up to 20 Cognizant email addresses. Subject and message are generated automatically and can be edited.</Typography>
      {error && <Alert severity="error">{error}</Alert>}{success && <Alert severity="success">{success}</Alert>}
      <Autocomplete
        multiple
        freeSolo
        options={[]}
        value={recipients}
        inputValue={recipientInput}
        onChange={(_, value) => setRecipients(normalizeRecipients(value))}
        onInputChange={(_, value) => setRecipientInput(value)}
        renderInput={(params) => <TextField {...params} label="Recipients" placeholder="Type or paste email addresses" helperText="Only @cognizant.com addresses are accepted. Separate multiple addresses with Enter, comma, or semicolon." />}
      />
      <TextField label="Subject" value={subject} onChange={(event) => setSubject(event.target.value)} inputProps={{ maxLength: 180 }} />
      <TextField label="Message" value={body} onChange={(event) => setBody(event.target.value)} multiline minRows={5} />
    </Stack></DialogContent>
    <DialogActions sx={{ px: 3, pb: 2.5 }}><Button onClick={onClose} disabled={sending}>{success ? "Close" : "Cancel"}</Button>{!success && <Button variant="contained" startIcon={sending ? <CircularProgress color="inherit" size={18} /> : <SendRoundedIcon />} onClick={send} disabled={sending || !hasRecipient}>{sending ? "Sending" : "Send email"}</Button>}</DialogActions>
  </Dialog>;
}
